import { useAccordion, AccordionItem } from "@/components/ui/Accordion";
import { ArrowRightIcon } from "@/components/common/Icons";
import { smoothScroll } from "@/utils/smoothScroll.js";

/**
 * Footer Component
 * Responsive footer with desktop grid and mobile accordion
 */
export function Footer() {
  const { toggle, isActive } = useAccordion();

  const acfData = window.post_template;

  const rawColumns = Array.isArray(acfData?.footer_columns)
    ? acfData.footer_columns
    : [];

  const footerColumns = rawColumns.map((col, index) => {
    const links = Array.isArray(col.column_links)
      ? col.column_links.map((linkItem) => {
          let href = "#";
          let target = undefined;
          let rel = undefined;

          const isCustom = linkItem.link_type === "custom_url";

          if (isCustom) {
            href = linkItem.custom_link || "#";

            if (!href.startsWith("#")) {
              target = "_blank";
              rel = "noopener noreferrer";
            }
          } else {
            href = linkItem.post_link || "#";
            target = undefined;
          }

          return {
            label: linkItem.tr_link_label || "Read More",
            href: href,
            target: target,
            rel: rel,
          };
        })
      : [];

    return {
      id: `footer-col-${index}`,
      title: col.tr_column_title || "Untitled",
      links: links,
    };
  });

  const handleScroll = (e, href) => {
    if (href && href.startsWith("#")) {
      e.preventDefault();
      const targetId = href.substring(1);
      smoothScroll(targetId, 1200, 0, "main-scroll-container");
    }
  };

  return (
    <footer className="bg-dark-green text-white pt-16 pb-8 px-8 max-md:pt-0 max-md:pb-4 max-md:px-4">
      <div className="max-w-[1200px] mx-auto">
        <div className="hidden md:grid grid-cols-4 gap-8">
          {footerColumns.map((column) => (
            <div key={column.id} className="flex flex-col gap-4">
              <h3 className="text-base font-semibold mb-2">{column.title}</h3>
              <ul className="list-none flex flex-col gap-3 p-0 m-0">
                {column.links.map((link, index) => (
                  <li key={index}>
                    <a
                      href={link.href}
                      target={link.target}
                      rel={link.rel}
                      onClick={(e) => handleScroll(e, link.href)}
                      className="text-white/70 text-sm transition-opacity hover:text-white"
                    >
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
          <div className="flex flex-col gap-4">
            <h3 className="text-base font-semibold mb-2">Connect</h3>
            <p className="m-0">Join Our Newsletter</p>
            <form className="relative max-w-[250px]">
              <input
                type="email"
                placeholder="Your Email"
                className="w-full py-3 pl-4 pr-10 rounded-full text-base border-none
                           bg-[#ffffff0e] text-white placeholder:text-white/70 outline-none"
              />
              <button
                type="submit"
                aria-label="Submit email"
                className="absolute right-0 top-0 bottom-0 bg-transparent border-none
                           pl-2 pr-2.5 cursor-pointer text-white group flex items-center justify-center"
              >
                <ArrowRightIcon
                  className="w-5 h-5 opacity-30 hover:opacity-100 transition-opacity"
                  color="white"
                />
              </button>
            </form>
          </div>
        </div>
        <div className="md:hidden">
          <div className="block">
            {footerColumns.map((column) => (
              <AccordionItem
                key={column.id}
                id={column.id}
                question={column.title}
                answer={column.links}
                isActive={isActive(column.id)}
                onToggle={toggle}
                variant="footer"
              />
            ))}
          </div>
          <div className="pt-5 mt-0 block">
            <p className="text-white text-base font-[480] mb-3 text-center">
              Join Our Newsletter
            </p>
            <form className="relative max-w-full mb-4">
              <input
                type="email"
                placeholder="Your Email"
                className="w-full py-4 pl-4 pr-12 rounded-full text-base border-none
                           bg-white/[0.05] text-white placeholder:text-white/50 outline-none"
              />
              <button
                type="submit"
                aria-label="Submit email"
                className="absolute right-0 top-0 bottom-0 bg-transparent border-none
                           px-4 cursor-pointer text-white group flex items-center justify-center"
              >
                <ArrowRightIcon
                  className="w-5 h-5 opacity-30 hover:opacity-100 transition-opacity"
                  color="white"
                />
              </button>
            </form>
          </div>
        </div>
      </div>
      <div
        className="p-6 text-[0.8rem] leading-[1.5] text-white/50 text-center
                     bg-white/[0.015] rounded-[1rem] mt-[3.2rem]
                     max-md:p-6 max-md:mt-4 max-md:text-[0.75rem] max-md:leading-[1.45]"
      >
        <p className="m-0 max-w-full hyphens-auto">
          These statements have not been evaluated by the Food and Drug
          Administration. This product is not intended to diagnose, treat, cure,
          or prevent any disease. Scientific information and study data
          referenced on this website are based on research conducted on
          individual ingredients, not on the finished product. Results may vary.
          Always consult your healthcare provider before using any dietary
          supplement.
        </p>
      </div>
    </footer>
  );
}

export default Footer;
